import React from "react";
import moment from "moment";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card, CardHeader, CardContent } from "../../components/ui/card";
import { Clock, AlertTriangle } from "lucide-react";

interface KanbanCardProps {
  id: string;
  client: ClientType;
}

// Define thresholds for lifecycle stages
const STAGE_THRESHOLDS: Record<string, number> = {
  prospect: 3,
  initial_outreach: 5,
  benefits_analysis: 7,
  proposal_sent: 14,
  customer: 30, // Adding a threshold for customer stage
};

// Phone number formatter
const formatPhoneNumber = (phone?: string) => {
  if (!phone) return "No phone";
  return phone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
};

const KanbanCard: React.FC<KanbanCardProps> = ({ id, client }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : 1,
  };

  const lifecycleStage = client.lifecycleStage as keyof typeof STAGE_THRESHOLDS;

  // Use actual data for production
  const lastUpdate = client?.last_lifecycle_update
    ? moment(client.last_lifecycle_update)
    : moment(); // Default to now if no update timestamp

  const now = moment();
  const daysSinceUpdate = now.diff(lastUpdate, "days");

  const maxDaysAllowed = STAGE_THRESHOLDS[lifecycleStage] ?? Infinity;
  const isOverdue = daysSinceUpdate > maxDaysAllowed;
  const isNearlyOverdue = daysSinceUpdate > maxDaysAllowed * 0.75 && !isOverdue;

  // Calculate days left (or overdue)
  const daysLeft = maxDaysAllowed - daysSinceUpdate;

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`
        shadow-sm border transition-all duration-200
        ${isDragging ? "scale-105 shadow-lg ring-2 ring-primary" : "hover:shadow-md"}
        ${
          isOverdue
            ? "bg-red-50 border-red-300"
            : isNearlyOverdue
              ? "bg-amber-50 border-amber-200"
              : "border-gray-200"
        }
        cursor-grab active:cursor-grabbing
      `}
    >
      <CardHeader
        className={`
          p-3 flex justify-between items-start
          ${
            isOverdue
              ? "bg-red-100"
              : isNearlyOverdue
                ? "bg-amber-50"
                : "bg-gray-50"
          }
        `}
      >
        <div className="font-semibold text-sm text-gray-800">
          {client.firstName} {client.lastName}
        </div>
        {isOverdue && (
          <div className="h-5 w-5 flex items-center justify-center rounded-full bg-red-100">
            <AlertTriangle className="h-3 w-3 text-red-600" />
          </div>
        )}
      </CardHeader>
      <CardContent className="p-3 text-sm">
        <p className="text-gray-600 truncate">{client.email || "No email"}</p>
        <p className="text-gray-500 truncate">
          {formatPhoneNumber(client.phoneNumber)}
        </p>

        <div className="flex flex-col mt-2 gap-1">
          <span
            className={`
              inline-block px-2 py-1 text-xs font-semibold rounded
              ${
                isOverdue
                  ? "bg-red-100 text-red-700"
                  : isNearlyOverdue
                    ? "bg-amber-100 text-amber-700"
                    : "bg-gray-100 text-gray-700"
              }
            `}
          >
            {client.lifecycleStage?.replace("_", " ") || "Unknown"}
          </span>

          {(isOverdue || isNearlyOverdue) && (
            <div
              className={`
              flex items-center text-xs font-medium mt-1 px-2 py-1 rounded
              ${isOverdue ? "text-red-700 bg-red-50" : "text-amber-700 bg-amber-50"}
            `}
            >
              <Clock className="h-3 w-3 mr-1" />
              {isOverdue ? (
                <span>Overdue by {Math.abs(daysLeft)} days</span>
              ) : (
                <span>{daysLeft} days left</span>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default KanbanCard;
